import { getValidatedLimitParam, getValidatedPageParam, useCustomToast } from '@elkaso-app/web-design';
import http from 'apis/config/http-service';
import { useQueryWrapper } from 'apis/config/use-api-wrapper';
import type { AxiosError } from 'axios';
import { useIsLoggedIn } from 'hooks/use-is-logged-in';
import { TParam, usePageParams } from 'hooks/use-page-params';
import { UseQueryOptions, UseQueryResult } from 'react-query';

const endpoint = 'ums/v1/cms/exclusive-deals';
export const getExclusiveDealsEndpointIdentifier = endpoint;

export interface ExclusiveDealProductDto {
  id: number;
  nameEn: string;
  nameAr: string;
  brandEn?: string | null;
  brandAr?: string | null;
  supplierId?: number;
  defaultPrice?: number;
  defaultUnit?: string;
  imageUrl?: string | null;
  specs?: string | null;
  origin?: string | null;
  state?: string | null;
  // null = no category assigned; a number = the assigned catalog category id.
  catalogCategoryId?: number | null;
  categoryTitle?: string | null;
}

export interface ExclusiveDealSupplierDto {
  id: number;
  name: string | null;
}

export type DealStatus = 'COMING_SOON' | 'ACTIVE' | 'EXPIRED';

export interface ExclusiveDealResponseDto {
  id: number;
  productId: number;
  cashbackPercent?: number | null;
  countryCode: string;
  isActive: boolean;
  status: DealStatus;
  dealPrice?: number;
  supplier: ExclusiveDealSupplierDto | null;
  createdAt: string;
  updatedAt: string;
  lastModifiedAt?: string | null;
  lastActiveAt?: string | null;
  productExpiryDate?: string | null;
  isNearExpiry?: boolean;
  product?: ExclusiveDealProductDto;
  // null = deal's supplier product isn't linked to a master product yet.
  masterProductId?: number | null;
}

interface ResponseData {
  data: ExclusiveDealResponseDto[];
  total: number;
  page: number;
  limit: number;
  pageCount: number;
}

type TData = ResponseData;
type TError = AxiosError;
type TQueryKey = [
  typeof endpoint,
  {
    page: TParam;
    limit: TParam;
    sort: TParam;
    countryCode: TParam;
    isActive: TParam;
    supplierId: TParam;
    productName: TParam;
    lastModifiedFrom: TParam;
    lastModifiedTo: TParam;
    lastActiveFrom: TParam;
    lastActiveTo: TParam;
  }
];
type Options = Omit<UseQueryOptions<unknown, TError, TData, TQueryKey>, 'queryKey' | 'queryFn'>;
type ReturnType = UseQueryResult<TData, TError>;

// BE expects split params (sortBy + sortOrder). FE uses single URL param (sort=field.dir).
// Adapt here to keep BaseSortButton + usePageParams convention untouched for every other list.
const SORT_FIELDS_ALLOWED = new Set([
  'createdAt',
  'supplierName',
  'productName',
  'isActive',
  'lastModifiedAt',
  'lastActiveAt',
]);
function splitSortParam(sort: TParam): { sortBy?: string; sortOrder?: 'ASC' | 'DESC' } {
  if (typeof sort !== 'string' || !sort) return {};
  const [field, dir] = sort.split('.');
  if (!SORT_FIELDS_ALLOWED.has(field)) return {};
  const sortOrder = dir?.toLowerCase() === 'desc' ? 'DESC' : dir?.toLowerCase() === 'asc' ? 'ASC' : undefined;
  if (!sortOrder) return {};
  return { sortBy: field, sortOrder };
}

export function useGetExclusiveDealsApi(options?: Options): ReturnType {
  const toast = useCustomToast();
  const page = getValidatedPageParam();
  const limit = getValidatedLimitParam();
  const isLoggedIn = useIsLoggedIn();
  const { getPageParams } = usePageParams();
  const {
    countryCode,
    isActive,
    sort,
    supplierId,
    productName,
    lastModifiedFrom,
    lastModifiedTo,
    lastActiveFrom,
    lastActiveTo,
  } = getPageParams();

  const queryFn = async () => {
    try {
      const { sortBy, sortOrder } = splitSortParam(sort);
      const { data } = await http.get(endpoint, {
        params: {
          page,
          limit,
          sortBy,
          sortOrder,
          countryCode,
          isActive,
          supplierId,
          productName,
          lastModifiedFrom,
          lastModifiedTo,
          lastActiveFrom,
          lastActiveTo,
        },
      });

      return data;
    } catch (error: any) {
      if (!error.response?.status || !error.response?.data?.message) throw error;

      toast({
        description: error.response.data.message,
        status: 'error',
      });

      throw error;
    }
  };

  return useQueryWrapper(
    [
      endpoint,
      {
        page,
        limit,
        sort,
        countryCode,
        isActive,
        supplierId,
        productName,
        lastModifiedFrom,
        lastModifiedTo,
        lastActiveFrom,
        lastActiveTo,
      },
    ],
    queryFn,
    {
      enabled: isLoggedIn,
      ...options,
    }
  );
}
